import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ButtonComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import { InputListModel } from '@libs/form/models';
import { DynamicInputItemDirective } from '../../../directives';
import { DYNAMIC_INPUT_ITEMS_MAP, GroupBaseComponent } from '../../../models';
import { InputListService } from '../../../services';
import { InputItemsMap } from '../../input';

@Component({
  selector: 'ui-input-list',
  templateUrl: './input-list.component.html',
  styleUrls: ['./input-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    ButtonComponent,
    IconComponent,
    DynamicInputItemDirective,
  ],
  providers: [
    InputListService,
    {
      provide: DYNAMIC_INPUT_ITEMS_MAP,
      useValue: InputItemsMap,
    },
  ],
  animations: [
    trigger('rowAnimation', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(86%)',
        }),
        animate('300ms ease-out', style({
          opacity: 1,
          transform: 'translateY(0)',
        })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({
          opacity: 0,
          transform: 'translateY(-86%)',
        })),
      ]),
    ]),
  ],
})
export class InputListComponent extends GroupBaseComponent implements OnInit, OnChanges {
  private readonly inputListService: InputListService = inject(InputListService);

  public static BUTTON_ITEM_ADD_LABEL: string = 'Hinzufügen';

  @Input() public override data!: InputListModel;

  public inputRows: InputListModel[] = [];
  public inputListTemplate!: InputListModel;

  public get itemAddLabel(): string {
    return this.data.addItemLabel ?? InputListComponent.BUTTON_ITEM_ADD_LABEL;
  }

  public ngOnInit(): void {
    this.addRow();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.inputListTemplate = this.inputListService.buildInputListTemplate(this.data);
    }
  }

  public addRow(): void {
    const rowData: InputListModel = this.inputRows.length === 0 ? this.data : this.inputListTemplate;

    this.inputRows.push(rowData);

    this.inputListService.addRowControl(this.control, this.inputRows.length - 1, rowData);
  }

  public removeRow(index: number): void {
    if (this.inputRows.length === 1) {
      return;
    }

    this.inputListService.removeRowControl(this.control, this.inputRows.length, index);
    this.inputRows.pop();
  }

  public getGroupControl(index: number): FormGroup {
    return this.control.get(index.toString()) as FormGroup;
  }
}
